import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { filter, switchMap, debounceTime, tap, map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PrivateAssetSettingsService } from './private-asset-settings.service';
import { PrivacyMember, PrivacySettingsDialogData, PrivacySettingsData, PrivacyTeam } from './privacy.model';
import { simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import { DataObject } from '@shared/models/data-object.model';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { CbreAlertService } from '@cbre/alerts';
import { PlainOption } from '@shared/models/icommons.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';

const UTC_EDP_OUTPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';
const ALLOWED_USER_CONFIDENTIALITY = '2';

@UntilDestroy()
@Component({
  selector: 'miq-private-asset-settings-dialog',
  templateUrl: './private-asset-settings-dialog.component.html',
  styleUrls: ['./private-asset-settings-dialog.component.scss'],
  providers: [PrivateAssetSettingsService],
  standalone: false,
})
export class PrivateAssetSettingsDialogComponent implements OnInit, OnDestroy {
  public loading = false;
  public userTeamList: Array<PrivacyMember | PrivacyTeam> = [];
  public privacySettingsFormGroup: UntypedFormGroup;
  public privacyStartDate: string;
  public privacyEndDateDisplay: string;
  public isRecordPrivate: boolean;
  public isUserAllowed = false;
  public privacyReasons$: Observable<PlainOption[]>;
  public minExpiryDate = new Date();

  private record: DataObject;
  private entity: string;
  private recordConfidentiality: string;
  private accessControl: DataObject;
  private privacyEndDate: string | Moment;
  private privacyReason: string;
  private addUsers: number[] = [];
  private deleteUsers: number[] = [];
  private addTeams: number[] = [];
  private deleteTeams: number[] = [];
  private existingUsers: PrivacyMember[] = [];
  private updatedSelectedUsers: Array<{
    user: PrivacyTeam | PrivacyMember;
  }> = [];
  private existingTeams: PrivacyTeam[] = [];
  private updatedSelectedTeams: Array<{
    team: PrivacyTeam | PrivacyMember;
  }> = [];
  public selectedUsersTeams: Array<PrivacyMember | PrivacyTeam> = [];
  private userId: number;

  constructor(
    public dialogRef: MatDialogRef<PrivateAssetSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: PrivacySettingsDialogData,
    private formBuilder: UntypedFormBuilder,
    private privateAssetSettingsService: PrivateAssetSettingsService,
    private cbreAlertService: CbreAlertService,
    private userInfoResolver: UserInfoResolver
  ) {
    this.initPrivacyData();
    this.createControls();
    this.addCurrentUser();
    this.privacySettingsFormGroup
      .get('searchFormControl')
      .valueChanges.pipe(
        untilDestroyed(this),
        debounceTime(400),
        filter(query => {
          if (!Boolean(query)) {
            this.userTeamList = this.sortUsersTeams(this.selectedUsersTeams.filter(item => item.selected));
            return false;
          }
          return true;
        }),
        tap(() => (this.loading = true)),
        switchMap((query: string) => this.privateAssetSettingsService.searchUsers(query))
      )
      .subscribe(
        ({ users, teams }: { users: PrivacyMember[]; teams: PrivacyTeam[] }) => {
          this.loading = false;
          this.userTeamList = this.sortUsersTeams([
            ...this.joinUserList(users || []),
            ...this.joinTeamList(teams || []),
          ]);
        },
        () => this.cbreAlertService.showErrorSnackBar_l10n('shared.errorWhileGettingInfo')
      );
  }

  public ngOnInit(): void {
    /* required by angular-l10n */
  }

  private initPrivacyData(): void {
    this.userId = this.userInfoResolver.resolve().id;
    this.privacyReasons$ = this.privateAssetSettingsService.getPrivacyReasons();
    if (this.data) {
      this.record = this.data.record || {};
      this.entity = this.data.entity;
      this.recordConfidentiality = this.record.confidentiality_f;
      this.accessControl = { ...(this.record.access_control || {}) };
      const miqProperties = this.record.miqProperties || {};
      this.privacyStartDate = new Date(this.accessControl.privacy_effective_date).toLocaleDateString();
      this.privacyEndDateDisplay = new Date(this.accessControl.privacy_expiration_date).toLocaleDateString();
      this.privacyEndDate = this.accessControl.privacy_expiration_date;
      this.privacyReason = this.accessControl.privacy_reason_type_desc;
      this.isRecordPrivate = this.accessControl.private_data_f;
      /* when the record is made private, userConfidentiality of user who do not have access is set as 0 and the
      recordConfidentiality is set to 2, userConfidentiality of the user who has access to the record is set to 2 */
      this.isUserAllowed =
        (this.isRecordPrivate
          ? miqProperties.userConfidentiality === ALLOWED_USER_CONFIDENTIALITY
          : miqProperties.userConfidentiality >= this.recordConfidentiality) && this.data.isPrivacySection;
      this.existingUsers = (this.accessControl.users || []).map(({ user }) => this.modifyUser(user));
      this.existingTeams = (this.accessControl.teams || []).map(({ team }) => ({
        ...team,
        name: team.team_name,
        selected: true,
      }));
      this.selectedUsersTeams = this.sortUsersTeams([...this.existingUsers, ...this.existingTeams]);
    }
  }

  private createControls(): void {
    this.privacySettingsFormGroup = this.formBuilder.group({
      expiryDate: [this.privacyEndDate],
      privacyReason: [this.privacyReason, [Validators.required]],
      searchFormControl: [null],
      privacyStatus: [this.isRecordPrivate ? 'on' : 'off'],
    });
    this.privacySettingsFormGroup.get('expiryDate').markAsTouched();
    if (!this.isUserAllowed) {
      this.privacySettingsFormGroup.disable();
    }
  }

  private addCurrentUser(): void {
    this.privacySettingsFormGroup
      .get('privacyStatus')
      .valueChanges.pipe(
        take(1),
        filter(status => status === 'on' && !this.isRecordPrivate),
        switchMap(() => this.privateAssetSettingsService.getCurrentUser(this.userId.toString())),
        map(user => this.modifyUser(user))
      )
      .subscribe((user: PrivacyMember) => {
        if (!this.userTeamList.some(({ id }) => id === user.id)) {
          this.userTeamList = [...this.userTeamList, user];
        }
        this.onUserSelected({ checked: true, user: user });
      });
  }

  private modifyUser(user: PrivacyMember): PrivacyMember {
    return {
      ...user,
      firstName: user.first_name,
      lastName: user.last_name,
      name: `${user.first_name} ${user.last_name}`,
      emailAddress: user.email,
      isUser: true,
      selected: true,
    };
  }

  private joinUserList(searchedUsers: PrivacyMember[]): PrivacyMember[] {
    searchedUsers = searchedUsers.map(searchedUser => ({
      ...searchedUser,
      firstName: searchedUser.first_name,
      lastName: searchedUser.last_name,
      name: `${searchedUser.first_name} ${searchedUser.last_name}`,
      emailAddress: searchedUser.email,
      isUser: true,
    }));
    return [
      ...searchedUsers.filter(searchedUser => {
        const isAlreadyExisting = this.existingUsers.some(
          existingUser =>
            existingUser.email != null && existingUser.email.toLowerCase() === searchedUser.email.toLowerCase()
        );
        return searchedUser.email != null && !isAlreadyExisting;
      }),
    ];
  }

  private joinTeamList(searchedTeams: PrivacyTeam[]): PrivacyTeam[] {
    searchedTeams = searchedTeams.map(searchedTeam => ({
      ...searchedTeam,
      name: searchedTeam.team_name,
    }));
    return [
      ...searchedTeams.filter(searchedTeam => {
        const isAlreadyExisting = this.existingTeams.some(
          existingTeam => existingTeam.id != null && existingTeam.id === searchedTeam.id
        );
        return searchedTeam.id != null && !isAlreadyExisting;
      }),
    ];
  }

  private sortUsersTeams(usersTeams: Array<PrivacyMember | PrivacyTeam>): Array<PrivacyMember | PrivacyTeam> {
    return usersTeams.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  }

  public onDateChange(event: MatDatepickerInputEvent<Moment>): void {
    const date = event.value;
    this.privacyEndDate = !simpleValueValidation(date) ? date : date.format(UTC_EDP_OUTPUT_FORMAT);
  }

  public onUserSelected({ checked: selected, user }: { checked: boolean; user: PrivacyMember }): void {
    if (selected) {
      if (!this.existingUsers.some(({ id }) => id === user.id)) {
        this.addUsers = [...this.addUsers, user.id];
      }
      this.deleteUsers = this.deleteUsers.filter(id => id !== user.id);
    } else {
      if (this.existingUsers.some(({ id }) => id === user.id)) {
        this.deleteUsers = [...this.deleteUsers, user.id];
      }
      this.addUsers = this.addUsers.filter(id => id !== user.id);
    }
    this.selectedUsersTeams = this.selectedUsersTeams.some(({ id }) => id === user.id)
      ? this.selectedUsersTeams.map(selectedUser =>
          user.id === selectedUser.id ? { ...selectedUser, selected: selected } : selectedUser
        )
      : [...this.selectedUsersTeams, { ...user, selected: selected }];
  }

  public onTeamSelected({ checked: selected, team }: { checked: boolean; team: PrivacyTeam }): void {
    if (selected) {
      if (!this.existingTeams.some(({ id }) => id === team.id)) {
        this.addTeams = [...this.addTeams, team.id];
      }
      this.deleteTeams = this.deleteTeams.filter(id => id !== team.id);
    } else {
      if (this.existingTeams.some(({ id }) => id === team.id)) {
        this.deleteTeams = [...this.deleteTeams, team.id];
      }
      this.addTeams = this.addTeams.filter(id => id !== team.id);
    }
    this.selectedUsersTeams = this.selectedUsersTeams.some(({ id }) => id === team.id)
      ? this.selectedUsersTeams.map(selectedTeam =>
          team.id === selectedTeam.id ? { ...selectedTeam, selected: selected } : selectedTeam
        )
      : [...this.selectedUsersTeams, { ...team, selected: selected }];
  }

  public savePrivacySettings(): void {
    if (this.isUserAllowed) {
      if (this.hasUserOrTeam() || this.privacyStatus === 'off') {
        const payload = this.createPrivacyRequest();
        this.privateAssetSettingsService.savePrivacySettings(payload).subscribe(
          () => {
            this.modifyRecord();
            this.dialogRef.close(this.record);
          },
          () => this.cbreAlertService.showErrorSnackBar_l10n('shared.errorSaving')
        );
      } else {
        this.cbreAlertService.showErrorSnackBar_l10n('shared.userTeamRequired');
      }
    } else {
      this.cbreAlertService.showWarningSnackBar_l10n('shared.notAllowed');
    }
  }

  private hasUserOrTeam(): boolean {
    this.updatedSelectedUsers = this.selectedUsersTeams
      .filter(element => element.isUser && element.selected)
      .map(user => ({
        user: user,
      }));
    this.updatedSelectedTeams = this.selectedUsersTeams
      .filter(element => !element.isUser && element.selected)
      .map(team => ({
        team: team,
      }));
    return !!(this.updatedSelectedUsers.length || this.updatedSelectedTeams.length);
  }

  private createPrivacyRequest(): PrivacySettingsData {
    return {
      id: this.record.entity.id,
      entity: this.entity,
      action: this.privacyStatus === 'on' ? 'enable' : 'disable',
      reason: this.privacySettingsFormGroup.get('privacyReason').value,
      expiryDate: this.privacyEndDate,
      addUsers: [...new Set(this.addUsers)],
      addTeams: [...new Set(this.addTeams)],
      deleteUsers: [...new Set(this.deleteUsers)],
      deleteTeams: [...new Set(this.deleteTeams)],
    };
  }

  private modifyRecord(): void {
    this.accessControl.private_data_f = this.privacyStatus === 'on';
    this.accessControl.privacy_expiration_date = this.privacyEndDate;
    this.accessControl.privacy_effective_date = this.accessControl.privacy_effective_date
      ? this.accessControl.privacy_effective_date
      : new Date();
    this.accessControl.privacy_reason_type_desc = this.privacySettingsFormGroup.get('privacyReason').value;
    this.accessControl.users = this.updatedSelectedUsers;
    this.accessControl.teams = this.updatedSelectedTeams;
    this.record.access_control = this.accessControl;
  }

  public get privacyStatus(): string {
    return this.privacySettingsFormGroup.controls['privacyStatus'].value;
  }

  public ngOnDestroy(): void {
    /* required by angular-l10n */
  }
}
